import * as React from "react";
import { Record } from "immutable";
import { useDispatch, useSelector } from "react-redux";

import { defaultValues } from "@config/defaultValues";

import { EXTRA_SERVICES_BOOKING, Task } from "@typings/booking";
import { MODALS } from "@typings/modals";
import { ORIGIN } from "@typings/globals";

import { ModalContentCalculateTime } from "@ui/Organisms/ModalContentCalculateTime";

import cleaningDuration from "@services/CleaningDurationService";

import { useTranslate } from "@hooks/useTranslate";
import { useConfig } from "@hooks/useConfig";
import { useTelemetry } from "@hooks/useTelemetry";
import { useLocalisation } from "@hooks/useLocalisation";

import { Modal } from "@app/containers";
import { setTask } from "@app/redux/booking/booking.actions";
import { IStore } from "@app/redux/reducers";
import {
  getTask,
  getHasServiceVouchers,
} from "@app/redux/booking/booking.selectors";
import { useModalStore } from "@app/hooks/useModalStore";

const CalculateTimeModal = React.memo(() => {
  const translate = useTranslate();
  const { hideModal, options } = useModalStore();
  const { elementClick } = useTelemetry();
  const { language, country } = useLocalisation();

  const { id } = options(MODALS.BOOKING_CALCULATE_TIME);

  const dispatch = useDispatch();

  const hasServiceVouchers = useSelector(getHasServiceVouchers);

  const durationMatrixDefault = useConfig(["booking", "durationMatrix"]);
  const serviceVouchersDurationMatrix = useConfig([
    "booking",
    "serviceVouchers",
    "durationMatrix",
  ]);

  const durationMatrix = hasServiceVouchers
    ? serviceVouchersDurationMatrix
    : durationMatrixDefault;

  const telemetryContext = {
    context: {
      modalTitle: translate("batmaid_pwa_booking.modal_calculate_time_title"),
    },
  };

  const homeCleaningTask: Record<Task> = useSelector((store: IStore) =>
    getTask(store, EXTRA_SERVICES_BOOKING.HOME_CLEANING),
  );

  const [cleaningValues, setCleaningValues] = React.useState<{
    bathrooms: number;
    bedrooms: number;
  }>({
    bathrooms: defaultValues.booking.bathroomNo,
    bedrooms: defaultValues.booking.bedroomNo,
  });

  React.useEffect(() => {
    setCleaningValues({
      bathrooms: homeCleaningTask.get("bathrooms")!,
      bedrooms: homeCleaningTask.get("bedrooms")!,
    });
  }, [JSON.stringify(homeCleaningTask), id]);

  return id ? (
    <Modal
      title={translate("batmaid_pwa_booking.modal_calculate_time_title")}
      id={MODALS.BOOKING_CALCULATE_TIME}
      submitText={translate("batmaid_pwa_generic.apply")}
      submitBtnColor="blue"
      submit={() => {
        elementClick(translate("batmaid_pwa_generic.apply"), {
          buttonId: "booking-calculate-time-modal-submit",
          ...telemetryContext,
        });

        hideModal(MODALS.BOOKING_CALCULATE_TIME);
        dispatch(
          setTask({
            service: EXTRA_SERVICES_BOOKING.HOME_CLEANING,
            selected: true,
            duration: cleaningDuration(
              cleaningValues.bedrooms,
              cleaningValues.bathrooms,
              durationMatrix,
            ),
            bedrooms: cleaningValues.bedrooms,
            bathrooms: cleaningValues.bathrooms,
          }),
        );
      }}
      close={() => {
        elementClick(translate("batmaid_pwa_generic.button_cancel"), {
          buttonId: "booking-calculate-time-modal-cancel",
          ...telemetryContext,
        });

        setCleaningValues({
          bathrooms: homeCleaningTask.get("bathrooms")!,
          bedrooms: homeCleaningTask.get("bedrooms")!,
        });
      }}
    >
      <ModalContentCalculateTime
        homeCleaningTask={homeCleaningTask}
        setTask={params => dispatch(setTask(params))}
        cleaningValues={cleaningValues}
        hasWhatsIncluded
        setCleaningValues={(values, buttonLabel, buttonId) => {
          setCleaningValues(values);
          elementClick(buttonLabel, {
            buttonId: `booking-calculate-time-modal-counter-${buttonId}`,
            context: {
              ...telemetryContext.context,
              displayedValues: values,
            },
          });
        }}
        origin={ORIGIN.BATMAID}
        language={language}
        country={country}
        translations={{
          bedroomsText: translate("batmaid_pwa_booking.bedrooms"),
          bedroomText: translate("batmaid_pwa_booking.bedroom"),
          bathroomsText: translate("batmaid_pwa_booking.bathrooms"),
          bathroomText: translate("batmaid_pwa_booking.bathroom"),
          recommendedTimeText: translate(
            "batmaid_pwa_booking.recommended_time",
            {
              count: String(
                cleaningDuration(
                  cleaningValues.bedrooms,
                  cleaningValues.bathrooms,
                  durationMatrix,
                ),
              ),
            },
          ),
          includedAreasText: translate("batmaid_pwa_booking.included_areas"),
          checkIncludedText: translate("batmaid_pwa_booking.check_included"),
        }}
      />
    </Modal>
  ) : null;
});

export { CalculateTimeModal };

CalculateTimeModal.displayName = "CalculateTimeModal";
